import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from 'components/icons';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional line under the title. */
  description?: string;
  /** Leading icon beside the title. */
  icon?: React.ComponentType<{ className?: string }>;
  size?: ModalSize;
  children: React.ReactNode;
  /** Pinned action row. Omit for content-only dialogs. */
  footer?: React.ReactNode;
  /** Set false for destructive confirms that shouldn't close on a stray click. */
  dismissOnOverlayClick?: boolean;
}

/**
 * Built on headlessui's Dialog, which was already a dependency with zero
 * usages. Every modal in the app was a hand-rolled `fixed inset-0` div — no
 * focus trap, no Escape handler, no focus restore, no body scroll lock, and
 * no aria-modal, so keyboard and screen-reader users could tab into the page
 * behind an open dialog.
 *
 * Dialog gives all of that: it traps focus, closes on Escape, restores focus
 * to the trigger, locks scroll, and wires aria-modal/labelledby/describedby
 * to Dialog.Title and Dialog.Description.
 */
const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  icon: Icon,
  size = 'md',
  children,
  footer,
  dismissOnOverlayClick = true,
}) => (
  <Transition show={open} as={Fragment}>
    {/* When overlay dismissal is off, Escape still closes — a keyboard user
        must never be trapped. Only the pointer path is disabled. */}
    <Dialog onClose={dismissOnOverlayClick ? onClose : () => undefined} className="relative z-50">
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-[1px]" aria-hidden="true" />
      </Transition.Child>

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 translate-y-2 sm:scale-[0.98]"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-2 sm:scale-[0.98]"
          >
            <Dialog.Panel
              className={`w-full ${sizeClass[size]} rounded-token-lg bg-surface-raised shadow-e3 ring-1 ring-border flex flex-col max-h-[90vh]`}
            >
              <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-border">
                <div className="min-w-0">
                  <Dialog.Title className="text-title text-content flex items-center gap-2">
                    {Icon && <Icon className="h-5 w-5 text-content-muted shrink-0" aria-hidden="true" />}
                    <span className="truncate">{title}</span>
                  </Dialog.Title>
                  {description && (
                    <Dialog.Description className="mt-1 text-body text-content-muted">
                      {description}
                    </Dialog.Description>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close dialog"
                  className="-m-1.5 p-1.5 rounded-token text-content-subtle hover:text-content hover:bg-surface-sunk focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              <div className="px-5 py-4 overflow-y-auto">{children}</div>

              {footer && (
                <div className="px-5 py-4 border-t border-border bg-surface-sunk flex flex-wrap items-center justify-end gap-2">
                  {footer}
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>
);

export default Modal;
